import { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { addDriver, getDriver } from "../../../actions/admin/driver";
import Alert from "../../layout/Alert";
import Spinner from "../../layout/Spinner";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminFormFullRow,
  AdminTextField,
  AdminTextAreaField,
  AdminToggleField,
  AdminRadioField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ChangeList, diffFields } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";
import { DRIVER_LIST, DRIVER_FIELDS, CONTRACT_OPTIONS } from "./AddDriver";

const UpdateDriver = ({ match }) => {
  const dispatch = useDispatch();
  const { loading, driver } = useSelector((state) => state.driver);

  const [formData, setFormData] = useState({
    _id: null,
    contract: "",
    name: "",
    email: "",
    phone: "",
    isfulltime: true,
    isactive: false,
  });
  const { contract, name, email, phone, isfulltime, isactive } = formData;

  // The stored record as the form saw it when it loaded — the "before" side of the
  // change summary.
  const savedFormData = useRef(null);

  useEffect(() => {
    dispatch(getDriver(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    const loadedFormData = {
      _id: loading || !driver?._id ? "" : driver._id,
      name: loading || !driver?.name ? "" : driver.name,
      contract: loading || !driver?.contract ? "" : driver.contract,
      email: loading || !driver?.email ? "" : driver.email,
      phone: loading || !driver?.phone ? "" : driver.phone,
      isfulltime: loading || !driver?.isfulltime ? false : driver.isfulltime,
      // This read `"" : driver.isactive`, so an inactive driver arrived as the empty
      // string and the toggle rendered uncontrolled.
      isactive: loading || !driver?.isactive ? false : driver.isactive,
    };

    savedFormData.current = loadedFormData;
    setFormData(loadedFormData);
  }, [loading, driver]);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const save = useSaveConfirmation({
    save: () => dispatch(addDriver(formData, null, true)),
  });

  const onSubmit = (e) => {
    e.preventDefault();

    const changes = diffFields(DRIVER_FIELDS, savedFormData.current || {}, formData);

    if (changes.length === 0) {
      dispatch(setAlert("Nothing to update -- this driver is unchanged.", "primary"));
      return;
    }

    save.request(changes);
  };

  const actions = <AdminBackLink to={DRIVER_LIST} />;

  if (loading) {
    return (
      <AdminPage title="Update Driver" actions={actions}>
        <Spinner />
      </AdminPage>
    );
  }

  return (
    <AdminPage title="Update Driver" actions={actions}>
      <form onSubmit={onSubmit} className="tw:max-w-[720px]">
        <AdminFormCard title="Driver status">
          <AdminToggleField
            name="isactive"
            label="Driver is active"
            description="Only active drivers can be assigned to a booking."
            checked={isactive}
            onChange={() => setFormData({ ...formData, isactive: !isactive })}
          />

          <AdminRadioField
            label="Driver is"
            name="isfulltime"
            value={isfulltime}
            options={CONTRACT_OPTIONS}
            onChange={(value) => setFormData({ ...formData, isfulltime: value })}
          />
        </AdminFormCard>

        <AdminFormCard title="Driver details" columns={2}>
          <AdminTextField
            name="name"
            label="Name"
            placeholder="Enter name"
            value={name}
            onChange={onChange}
            required
          />

          <AdminTextField
            name="email"
            label="Email"
            type="email"
            placeholder="Enter email"
            value={email}
            onChange={onChange}
          />

          <AdminTextField
            name="phone"
            label="Phone number"
            type="tel"
            placeholder="Enter phone number"
            value={phone}
            onChange={onChange}
          />

          <AdminFormFullRow>
            <AdminTextAreaField
              name="contract"
              label="Contract"
              placeholder="Enter contract"
              value={contract}
              onChange={onChange}
            />
          </AdminFormFullRow>
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Update</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Apply these driver changes?"
        description="These take effect straight away, on this driver's future assignments."
        confirmLabel="Update Driver"
        busyLabel="Saving..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ChangeList changes={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Driver updated"
        description="These are live now, on this driver's future assignments."
        onClose={save.dismiss}
      >
        <ChangeList changes={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default withRouter(UpdateDriver);
